/**
 * CustomerReportsPage (iter380)
 * ------------------------------
 * One page, four tabs:
 *  1. Ledger            — running balance per customer, every sale + receipt
 *  2. Outstanding/Aging — 0-30 / 31-60 / 61-90 / 90+ buckets
 *  3. Sales by Customer — revenue · cash/bank/credit split · avg ticket
 *  4. Top Customers     — top N by revenue / balance / count, with share-of-revenue %
 *
 * Every tab honours the same date-range picker at the top and has its
 * own CSV export (tenant-scoped on the backend).
 */
import { cur } from '@/lib/currency';
import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Users, Download, CalendarDays, AlertCircle, TrendingUp, Award, Wallet } from 'lucide-react';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar } from '@/components/reports/ReportFilterBar';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { VatModeToggle } from '@/components/common/VatModeToggle';
import { useVatModeStore } from '@/stores/vatModeStore';
import { useBranchStore } from '@/stores';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday, toLocalYmd } from '@/lib/dateRange';

// ---------- Date range presets -------------------------------------------------
const today = () => localToday();
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toLocalYmd(d); };
const PRESETS = [
  { id: 'today',      label: 'Today',      start: today,                end: today },
  { id: 'week',       label: 'This Week',  start: () => daysAgo(7),     end: today },
  { id: 'month',      label: 'This Month', start: () => { const d = new Date(); d.setDate(1); return toLocalYmd(d); }, end: today },
  { id: 'last_month', label: 'Last Month', start: () => { const d = new Date(); d.setMonth(d.getMonth()-1,1); return toLocalYmd(d); }, end: () => { const d = new Date(); d.setDate(0); return toLocalYmd(d); } },
  { id: 'year',       label: 'This Year',  start: () => `${new Date().getFullYear()}-01-01`, end: today },
  { id: 'all',        label: 'All Time',   start: () => '',             end: () => '' },
];

const money = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// -----------------------------------------------------------------------------
// Row sub-components (iter381)
// -----------------------------------------------------------------------------
// Emergent's dev-tool wraps every JSX element in an instrumentation <span>. When
// we inlined <tr> expressions directly inside <tbody>, that <span> landed
// between <tbody> and <tr> — triggering React "cannot be a child of <tbody>"
// hydration warnings in the console. By lifting each row into its own component
// the injected <span> sits AROUND the component's top-level element in the
// VDOM, not inside the table structure, so the HTML stays valid.
function LedgerEntryRow({ entry }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5">{entry.date?.slice(0, 10)}</td>
      <td className="py-1.5"><Badge variant="outline" className="text-[10px]">{entry.type}</Badge></td>
      <td className="py-1.5 text-xs text-muted-foreground max-w-[220px] truncate" title={entry.items || ''}>{entry.items || ''}</td>
      <td className="py-1.5 text-right text-rose-600">{entry.debit ? money(entry.debit) : ''}</td>
      <td className="py-1.5 text-right text-emerald-600">{entry.credit ? money(entry.credit) : ''}</td>
      <td className="py-1.5 text-right font-semibold">{money(entry.balance)}</td>
    </tr>
  );
}

function LedgerCustomerCard({ ledger }) {
  return (
    <Card className="border-border">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <div className="font-semibold">{ledger.customer_name}</div>
          <div className="text-xs text-muted-foreground">{ledger.phone || '—'} · {ledger.row_count} entries</div>
        </div>
        <Badge className={ledger.closing_balance > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}>Balance: {money(ledger.closing_balance)}</Badge>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-muted-foreground">
                <th className="text-left py-1.5">Date</th>
                <th className="text-left py-1.5">Type</th>
                <th className="text-left py-1.5">Items</th>
                <th className="text-right py-1.5">Debit</th>
                <th className="text-right py-1.5">Credit</th>
                <th className="text-right py-1.5">Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledger.rows.map((r, i) => <LedgerEntryRow key={i} entry={r} />)}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function AgingDataRow({ row }) {
  return (
    <tr className="border-b border-border/50" data-testid="aging-row">
      <td className="p-2.5">
        <div className="font-medium">{row.customer_name}</div>
        <div className="text-xs text-muted-foreground">{row.phone}</div>
      </td>
      <td className="text-right p-2.5">{money(row.current)}</td>
      <td className="text-right p-2.5">{money(row.b30)}</td>
      <td className="text-right p-2.5">{money(row.b60)}</td>
      <td className="text-right p-2.5">{money(row.b90)}</td>
      <td className="text-right p-2.5 text-rose-600 font-semibold">{money(row.b90plus)}</td>
      <td className="text-right p-2.5 font-bold">{money(row.total)}</td>
    </tr>
  );
}

function AgingTotalsRow({ totals }) {
  return (
    <tr className="bg-muted font-bold">
      <td className="p-2.5">TOTALS</td>
      <td className="text-right p-2.5">{money(totals.current)}</td>
      <td className="text-right p-2.5">{money(totals.b30)}</td>
      <td className="text-right p-2.5">{money(totals.b60)}</td>
      <td className="text-right p-2.5">{money(totals.b90)}</td>
      <td className="text-right p-2.5 text-rose-600">{money(totals.b90plus)}</td>
      <td className="text-right p-2.5">{money(totals.total)}</td>
    </tr>
  );
}

function SalesDataRow({ row }) {
  const inclVat = useVatModeStore((s) => s.inclVat);
  return (
    <tr className="border-b border-border/50" data-testid="sales-row">
      <td className="p-2.5"><div className="font-medium">{row.customer_name}</div><div className="text-xs text-muted-foreground">{row.phone}</div></td>
      <td className="text-right p-2.5 font-semibold">{money(inclVat ? row.total_sales : row.net_sales ?? row.total_sales)}</td>
      <td className="text-right p-2.5 text-muted-foreground">{money(row.vat || 0)}</td>
      <td className="text-right p-2.5">{money(row.cash)}</td>
      <td className="text-right p-2.5">{money(row.bank)}</td>
      <td className="text-right p-2.5 text-rose-600">{money(row.credit)}</td>
      <td className="text-right p-2.5">{row.count}</td>
      <td className="text-right p-2.5">{money(row.avg_ticket)}</td>
    </tr>
  );
}

function SalesTotalsRow({ totals }) {
  const inclVat = useVatModeStore((s) => s.inclVat);
  return (
    <tr className="bg-muted font-bold">
      <td className="p-2.5">TOTALS</td>
      <td className="text-right p-2.5" data-testid="cr-sales-total">{money(inclVat ? totals.total_sales : totals.net_sales ?? totals.total_sales)}</td>
      <td className="text-right p-2.5">{money(totals.vat || 0)}</td>
      <td className="text-right p-2.5">{money(totals.cash)}</td>
      <td className="text-right p-2.5">{money(totals.bank)}</td>
      <td className="text-right p-2.5">{money(totals.credit)}</td>
      <td className="text-right p-2.5">{totals.count}</td>
      <td />
    </tr>
  );
}

function TopDataRow({ row, rank, showBalance }) {
  return (
    <tr className="border-b border-border/50" data-testid="top-row">
      <td className="p-2.5 font-bold text-muted-foreground">#{rank}</td>
      <td className="p-2.5"><div className="font-medium">{row.customer_name}</div><div className="text-xs text-muted-foreground">{row.phone}</div></td>
      <td className="text-right p-2.5 font-semibold">{money(row.total_sales)}</td>
      <td className="text-right p-2.5">{row.count}</td>
      <td className="text-right p-2.5">{money(row.avg_ticket)}</td>
      {showBalance && <td className="text-right p-2.5 text-rose-600">{money(row.balance || 0)}</td>}
      <td className="text-right p-2.5">
        <Badge className="bg-violet-100 text-violet-700">{row.share_of_revenue}%</Badge>
      </td>
    </tr>
  );
}

function EmptyRow({ colSpan, label }) {
  return <tr><td colSpan={colSpan} className="p-6 text-center text-muted-foreground">{label}</td></tr>;
}

// ---------- CSV helper ---------------------------------------------------------

const arraySpec = (title, headers, rows) => ({
  title: title.replace(/-/g, ' '), filename: title,
  cols: headers.map((h, i) => ({ key: String(i), label: h })),
  rows: rows.map((r) => Object.fromEntries(r.map((v, i) => [String(i), v]))),
});

export default function CustomerReportsPage() {
  const [tab, setTab] = useState('ledger');
  const [preset, setPreset] = useState('month');
  const [startDate, setStartDate] = useState(PRESETS[2].start());
  const [endDate, setEndDate] = useState(PRESETS[2].end());
  const [branchFilter, setBranchFilter] = useState([]);
  const { branches, fetchBranches } = useBranchStore();

  // data
  const [ledger, setLedger] = useState(null);
  const [aging, setAging] = useState(null);
  const [sales, setSales] = useState(null);
  const inclVat = useVatModeStore((s) => s.inclVat);
  const [top, setTop] = useState(null);
  const [topMetric, setTopMetric] = useState('revenue');
  const [topLimit, setTopLimit] = useState(20);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p) { setStartDate(p.start()); setEndDate(p.end()); }
  };

  const params = useMemo(() => {
    const p = {};
    if (startDate) p.start_date = startDate;
    if (endDate)   p.end_date = endDate;
    if (branchFilter.length === 1) p.branch_id = branchFilter[0];
    else if (branchFilter.length > 1) p.branch_ids = branchFilter.join(',');
    return p;
  }, [startDate, endDate, branchFilter]);

  // fetchers — one per tab, only the active tab runs to keep it snappy
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        if (tab === 'ledger') {
          const { data } = await api.get('/reports/customers/ledger', { params });
          setLedger(data);
        } else if (tab === 'aging') {
          const { data } = await api.get('/reports/customers/aging', { params });
          setAging(data);
        } else if (tab === 'sales') {
          const { data } = await api.get('/reports/customers/sales', { params });
          setSales(data);
        } else if (tab === 'top') {
          const { data } = await api.get('/reports/customers/top', { params: { ...params, metric: topMetric, limit: topLimit } });
          setTop(data);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load report');
      } finally { setLoading(false); }
    };
    run();
  }, [tab, startDate, endDate, branchFilter, topMetric, topLimit]);

  // ---------- Export handlers ---------
  const exportLedger = () => {
    if (!ledger) return arraySpec('ledger', [], []);
    const rows = [];
    ledger.ledgers.forEach((l) => {
      rows.push([l.customer_name, l.phone || '', '', '', '', '', money(l.closing_balance), '']);
      l.rows.forEach((r) => rows.push(['', '', r.date?.slice(0,10) || '', r.type, money(r.debit), money(r.credit), money(r.balance), r.items || '']));
    });
    return arraySpec('customer-ledger', ['Customer', 'Phone', 'Date', 'Type', 'Debit', 'Credit', 'Balance', 'Items'], rows);
  };
  const exportAging = () => {
    if (!aging) return arraySpec('aging', [], []);
    const rows = aging.rows.map((r) => [r.customer_name, r.phone, r.current, r.b30, r.b60, r.b90, r.b90plus, r.total]);
    rows.push(['TOTALS', '', aging.totals.current, aging.totals.b30, aging.totals.b60, aging.totals.b90, aging.totals.b90plus, aging.totals.total]);
    return arraySpec('customer-aging', ['Customer', 'Phone', 'Current', '1-30', '31-60', '61-90', '90+', 'Total Outstanding'], rows);
  };
  const exportSales = () => {
    if (!sales) return arraySpec('sales', [], []);
    const sv = (r) => (inclVat ? r.total_sales : r.net_sales ?? r.total_sales);
    const rows = sales.rows.map((r) => [r.customer_name, r.phone, sv(r), r.vat || 0, r.cash, r.bank, r.credit, r.count, r.avg_ticket, r.first_sale?.slice(0,10)||'', r.last_sale?.slice(0,10)||'']);
    rows.push(['TOTALS', '', sv(sales.totals), sales.totals.vat || 0, sales.totals.cash, sales.totals.bank, sales.totals.credit, sales.totals.count, '', '', '']);
    return arraySpec('sales-by-customer', ['Customer', 'Phone', inclVat ? 'Sales (Incl. VAT)' : 'Sales (Excl. VAT)', 'VAT', 'Cash (incl. VAT)', 'Bank (incl. VAT)', 'Credit (incl. VAT)', '# Sales', 'Avg Ticket', 'First Sale', 'Last Sale'], rows);
  };
  const exportTop = () => {
    if (!top) return arraySpec('top', [], []);
    const rows = top.rows.map((r, i) => [i + 1, r.customer_name, r.phone, r.total_sales, r.count, r.avg_ticket, r.balance ?? '', r.share_of_revenue + '%']);
    return arraySpec('top-customers', ['#', 'Customer', 'Phone', 'Total Sales', '# Sales', 'Avg Ticket', 'Balance', 'Share'], rows);
  };

  return (
    <DashboardLayout title="Customer Reports">
      <div className="space-y-3 mb-6">
        <ReportHeader icon={Users} title="Customer Reports" subtitle="Ledger · Aging · Sales · Top Customers — date-range aware" testId="cr-header" />
        <ReportFilterBar testId="cr-filter" branches={branches} branchIds={branchFilter} onBranchIds={setBranchFilter}
          range={{ start: startDate, end: endDate }} onRange={(r) => { setStartDate(r.start); setEndDate(r.end); setPreset('custom'); }} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 md:grid-cols-4 rounded-xl">
          <TabsTrigger value="ledger" data-testid="cr-tab-ledger"><Wallet size={14} className="mr-1.5" />Ledger</TabsTrigger>
          <TabsTrigger value="aging"  data-testid="cr-tab-aging"><AlertCircle size={14} className="mr-1.5" />Aging</TabsTrigger>
          <TabsTrigger value="sales"  data-testid="cr-tab-sales"><TrendingUp size={14} className="mr-1.5" />Sales by Customer</TabsTrigger>
          <TabsTrigger value="top"    data-testid="cr-tab-top"><Award size={14} className="mr-1.5" />Top Customers</TabsTrigger>
        </TabsList>

        {/* --- Ledger ---------------------------------------------------- */}
        <TabsContent value="ledger" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : ledger ? `${ledger.summary.customer_count} customers · ${money(ledger.summary.total_outstanding)} outstanding` : '—'}
            </div>
            <ExportTrio spec={exportLedger} disabled={!ledger} testId="cr-export-ledger" />
          </div>
          <div className="space-y-4">
            {(ledger?.ledgers || []).map((l) => <LedgerCustomerCard key={l.customer_id} ledger={l} />)}
            {ledger && ledger.ledgers.length === 0 && (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No ledger data in this range.</CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* --- Aging ----------------------------------------------------- */}
        <TabsContent value="aging" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">{loading ? 'Loading…' : aging ? `${aging.customer_count} customers · ${money(aging.totals.total)} outstanding` : '—'}</div>
            <ExportTrio spec={exportAging} disabled={!aging} testId="cr-export-aging" />
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2.5">Customer</th>
                    <th className="text-right p-2.5">Current</th>
                    <th className="text-right p-2.5">1-30</th>
                    <th className="text-right p-2.5">31-60</th>
                    <th className="text-right p-2.5">61-90</th>
                    <th className="text-right p-2.5 text-rose-600">90+</th>
                    <th className="text-right p-2.5">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(aging?.rows || []).map((r) => <AgingDataRow key={r.customer_id} row={r} />)}
                  {aging && aging.rows.length > 0 && <AgingTotalsRow totals={aging.totals} />}
                  {aging && aging.rows.length === 0 && <EmptyRow colSpan={7} label="No outstanding balances." />}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Sales by Customer ---------------------------------------- */}
        <TabsContent value="sales" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">{loading ? 'Loading…' : sales ? `${sales.customer_count} customers · ${money(sales.totals.total_sales)} total · ${sales.totals.count} sales` : '—'}</div>
            <div className="flex items-center gap-2"><VatModeToggle /><ExportTrio spec={exportSales} disabled={!sales} testId="cr-export-sales" /></div>
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2.5">Customer</th>
                    <th className="text-right p-2.5">{inclVat ? 'Sales (Incl. VAT)' : 'Sales (Excl. VAT)'}</th>
                    <th className="text-right p-2.5">VAT</th>
                    <th className="text-right p-2.5">Cash</th>
                    <th className="text-right p-2.5">Bank</th>
                    <th className="text-right p-2.5">Credit</th>
                    <th className="text-right p-2.5"># Sales</th>
                    <th className="text-right p-2.5">Avg Ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {(sales?.rows || []).map((r) => <SalesDataRow key={r.customer_id} row={r} />)}
                  {sales && sales.rows.length > 0 && <SalesTotalsRow totals={sales.totals} />}
                  {sales && sales.rows.length === 0 && <EmptyRow colSpan={7} label="No sales in this range." />}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* --- Top Customers -------------------------------------------- */}
        <TabsContent value="top" className="mt-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="text-sm text-muted-foreground mr-auto">{loading ? 'Loading…' : top ? `Top ${top.rows.length} by ${top.metric}` : '—'}</div>
            <Select value={topMetric} onValueChange={setTopMetric}>
              <SelectTrigger className="w-40 rounded-xl" data-testid="cr-top-metric"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="revenue">By Revenue</SelectItem>
                <SelectItem value="balance">By Balance</SelectItem>
                <SelectItem value="count">By # Sales</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(topLimit)} onValueChange={(v) => setTopLimit(Number(v))}>
              <SelectTrigger className="w-28 rounded-xl" data-testid="cr-top-limit"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[5,10,20,50,100].map((n) => <SelectItem key={n} value={String(n)}>Top {n}</SelectItem>)}
              </SelectContent>
            </Select>
            <ExportTrio spec={exportTop} disabled={!top} testId="cr-export-top" />
          </div>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase">
                  <tr>
                    <th className="text-left p-2.5 w-12">#</th>
                    <th className="text-left p-2.5">Customer</th>
                    <th className="text-right p-2.5">Total Sales</th>
                    <th className="text-right p-2.5"># Sales</th>
                    <th className="text-right p-2.5">Avg Ticket</th>
                    {topMetric === 'balance' && <th className="text-right p-2.5">Balance</th>}
                    <th className="text-right p-2.5">Share %</th>
                  </tr>
                </thead>
                <tbody>
                  {(top?.rows || []).map((r, i) => <TopDataRow key={r.customer_id} row={r} rank={i + 1} showBalance={topMetric === 'balance'} />)}
                  {top && top.rows.length === 0 && <EmptyRow colSpan={topMetric === 'balance' ? 7 : 6} label="No data." />}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
